import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { staffInventoryTourId, staffInventorySteps } from '@/tours/staff';
import toast from 'react-hot-toast';
import { ClipboardList, Plus, FileText } from 'lucide-react';
import { Card, Button, LoadingSpinner, EmptyState } from '@/components/ui';
import { StockTable, CheckFlow } from '@/components/features/inventory';
import { inventoryApi } from '@/services/api/inventoryApi';
import { queryKeys } from '@/services/api/queryClient';
import type { InventoryCheck } from '@/types/models';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const InventoryPage = () => {
  useTourAutoStart({ tourId: staffInventoryTourId, steps: staffInventorySteps });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeCheck, setActiveCheck] = useState<InventoryCheck | null>(null);

  const { data: stockData, isLoading: loadingStock } = useQuery({
    queryKey: queryKeys.stock,
    queryFn: () => inventoryApi.getStock(),
  });

  const { data: sheetsData } = useQuery({
    queryKey: queryKeys.deliverySheets,
    queryFn: () => inventoryApi.listDeliverySheets({ page: 1, limit: 10 }),
  });

  const startCheckMutation = useMutation({
    mutationFn: () => inventoryApi.startCheck(),
    onSuccess: (check) => {
      toast.success('Инвентаризация начата');
      setActiveCheck(check);
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при начале инвентаризации')),
  });

  const recordItemMutation = useMutation({
    mutationFn: (data: { product_id: number; actual_quantity: number; notes?: string }) => {
      if (!activeCheck) throw new Error('Нет активной проверки');
      return inventoryApi.recordCheckItem(activeCheck.id, data);
    },
    onSuccess: (item) => {
      if (activeCheck) {
        setActiveCheck((prev) => {
          if (!prev) return prev;
          return { ...prev, items: [...prev.items, item] };
        });
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при записи')),
  });

  const completeCheckMutation = useMutation({
    mutationFn: () => {
      if (!activeCheck) throw new Error('Нет активной проверки');
      return inventoryApi.completeCheck(activeCheck.id);
    },
    onSuccess: () => {
      toast.success('Инвентаризация завершена');
      setActiveCheck(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.stock });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryChecks });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при завершении')),
  });

  const stockLevels = stockData?.data ?? [];
  const sheets = sheetsData?.data ?? [];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Инвентаризация</h1>
          <p className="text-sm text-gray-500 mt-1">Проверка наличия товаров на складе</p>
        </div>
        {!activeCheck && (
          <Button data-tour="staff:inventory-check-btn" onClick={() => startCheckMutation.mutate()} loading={startCheckMutation.isPending} className="self-start sm:self-auto">
            <Plus className="w-4 h-4" />
            Начать проверку
          </Button>
        )}
      </div>

      {activeCheck ? (
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">
                  Проверка #{activeCheck.id}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Статус: {activeCheck.status === 'IN_PROGRESS' ? 'В процессе' : 'Завершена'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveCheck(null)}
              >
                Отмена
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <CheckFlow
              checkId={activeCheck.id}
              stockLevels={stockLevels}
              checkedItems={activeCheck.items}
              onRecordItem={async (data) => {
                await recordItemMutation.mutateAsync(data);
              }}
              onComplete={async () => {
                await completeCheckMutation.mutateAsync();
              }}
              isRecording={recordItemMutation.isPending}
              isCompleting={completeCheckMutation.isPending}
            />
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <Card.Header>
                <h2 className="font-semibold text-gray-900">Текущие запасы</h2>
              </Card.Header>
               <Card.Body className="p-0">
                <div data-tour="staff:inventory-stock">
                {loadingStock ? (
                  <LoadingSpinner className="py-12" />
                ) : stockLevels.length === 0 ? (
                  <EmptyState
                    icon={<ClipboardList className="w-8 h-8" />}
                    title="Нет данных о запасах"
                    description="Запасы появятся после приёмки поставок"
                  />
                ) : (
                  <StockTable items={stockLevels} />
                )}
                </div>
              </Card.Body>
            </Card>
          </div>

          <div>
            <Card>
              <Card.Header>
                <h2 className="font-semibold text-gray-900">Листы выдачи</h2>
              </Card.Header>
              <Card.Body>
                {sheets.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Нет листов выдачи</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sheets.map((sheet) => (
                      <button
                        key={sheet.id}
                        onClick={() => navigate(`/delivery-sheets/${sheet.id}`)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all text-left"
                      >
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">Лист #{sheet.id}</p>
                          <p className="text-xs text-gray-500">{sheet.items.length} позиций</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
